import { Tabs } from '@dynamika-shared/ui/Tabs/Tabs';
import { createContext, FC, useEffect, useMemo, useState } from 'react';
import { Outlet, useParams, useLocation } from 'react-router-dom';
import { generateTabsItems } from '@utils/generateTabsItems';
import { clinicalTrialFormsRoutes } from '@router/ClinicalTrial.routes';
import { SubPageHeader } from '@components/SubPageHeader/SubPageHeader';
import { drawers } from 'src/pages/ClinicalTrial/ClinicalTrialForms/drawers';
import { VisitConfig } from '@dynamika-shared/types/visits';
import { useQuestionnairesSlice } from '@restore/questionnaires/questionnaires.slice';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import { StudyStatusEnum } from '@dynamika-shared/types/status';
import { useAuthSlice } from '@restore/auth/auth.slice';
import { UserRoles } from '@dynamika-shared/types/auth';

export type TemplateContextType = {
  currentTemplateType?: keyof typeof drawers;
  setCurrentTemplateType: (templateType: keyof typeof drawers) => void;
  currentVisit?: VisitConfig;
  setCurrentVisit: (visit: VisitConfig) => void;
};

export const TemplateContext = createContext<TemplateContextType | undefined>(
  undefined
);

export const ClinicalTrialForms: FC = () => {
  const { pathname } = useLocation();
  const { id } = useParams();

  const isPM = useAuthSlice(s => s?.data?.selectedRole === UserRoles.PROJECT_MANAGER);

  const [currentTemplateType, setCurrentTemplateType] =
    useState<keyof typeof drawers>();
  const [currentVisit, setCurrentVisit] = useState<VisitConfig>();

  const clinicalTrial = useClinicalTrialSlice(state => state.data);

  const [fetchClinicalTrialQuestionnaires, fetchQuestionnaires] =
    useQuestionnairesSlice((_, actions) => [
      actions.fetchClinicalTrialQuestionnaires,
      actions.fetchQuestionnaires,
    ]);

  const context = useMemo(
    () => ({
      currentTemplateType,
      setCurrentTemplateType,
      currentVisit,
      setCurrentVisit,
    }),
    [currentTemplateType, setCurrentTemplateType, currentVisit, setCurrentVisit]
  );

  const Drawer = useMemo(
    () => (currentTemplateType ? drawers[currentTemplateType] : () => <></>),
    [currentTemplateType]
  );

  const items = useMemo(
    () =>
      generateTabsItems({
        routes: clinicalTrialFormsRoutes,
        currentPathname: '',
        id,
        disableStyles: true,
      }),
    [id]
  );

  const currentPathname = useMemo(() => {
    const chunks = pathname.split('/');
    return chunks?.[chunks.length - 1];
  }, [pathname]);

  useEffect(() => {
    fetchClinicalTrialQuestionnaires({ id });
    fetchQuestionnaires({});
  }, []);

  return (
    <div className={'flex flex-col h-full'}>
      <SubPageHeader
        title='Forms'
        action={() => setCurrentTemplateType('template')}
        showButton={
          currentPathname === 'management' &&
          clinicalTrial?.status === StudyStatusEnum.draft &&
          !isPM
        }
        buttonText='Template'
        headerClassName={'mb-3 h-6'}
      />
      <Tabs activeKey={currentPathname} items={items} mode={'button'} />
      <div className={'grow'}>
        <TemplateContext.Provider value={context}>
          <Outlet />
          {!!currentTemplateType && (
            <Drawer
              close={() => {
                setCurrentTemplateType(undefined);
              }}
            />
          )}
        </TemplateContext.Provider>
      </div>
    </div>
  );
};
